import './client/ui/styles.css';
import { SceneManager } from './client/rendering/SceneManager';
import { HudManager } from './client/ui/HudManager';
import { InputManager } from './client/input/InputManager';
import { ClientNetManager } from './client/networking/ClientNetManager';
import { GameClient } from './client/game/GameClient';
import { AudioManager } from './client/audio/AudioManager';
import { DEFAULT_VISUAL_QUALITY_PRESET } from './client/rendering/visualConfig';
import { DEFAULT_WORLD_THEME } from './client/rendering/worldTheme';
import { GameMode } from './shared/types';
import { DEFAULT_LOADOUT } from './shared/constants';
import { isAdminMode } from './client/util/url';

const canvas = document.getElementById('game-canvas') as HTMLCanvasElement;
const hudRoot = document.getElementById('hud-root') as HTMLElement;

const visualQualityPreset = DEFAULT_VISUAL_QUALITY_PRESET;
const worldTheme = DEFAULT_WORLD_THEME;

const scene = new SceneManager(canvas, visualQualityPreset, worldTheme);
const hud = new HudManager(hudRoot);
const input = new InputManager(scene, {
    adminMode: isAdminMode(),
});
const net = new ClientNetManager();
const audio = new AudioManager();
const game = new GameClient({
    scene,
    hud,
    input,
    net,
    audio,
    qualityPreset: visualQualityPreset,
    worldTheme,
});

// Focus the canvas so keyboard events go to it (touch-action:none + tabindex=-1
// makes it focusable). Required for FPS-mode keyboard responsiveness when the
// page first loads.
canvas.focus();

// Suppress Safari iOS pinch-to-zoom gestures on the canvas (non-standard
// events, Safari-only) so two-finger pinches don't zoom the page mid-game.
for (const evt of ['gesturestart', 'gesturechange', 'gestureend']) {
    canvas.addEventListener(evt as any, (e: Event) => e.preventDefault());
}

// Wire network observables to the HUD badge + reconnect overlay.
net.onConnectionState((state) => hud.setConnectionState(state));
net.onPing((ms) => hud.setPing(ms));

const urlParams = new URLSearchParams(window.location.search);
const modeParam = urlParams.get('mode');
const presetMode: GameMode = modeParam === GameMode.Br ? GameMode.Br : GameMode.Free;
const prefilledUsername = urlParams.get('username')?.trim() ?? '';

const startGame = (username: string, mode: GameMode, loadout: number[]) => {
    // Kick the connect immediately; `scene.init()` is awaited inside
    // `startLoop` on the first tick, so rendering catches up once the
    // WebGPU device is ready.
    game.start(username, mode, loadout);
};

const handleUsernameSubmit = (username: string, mode: GameMode) => {
    // Username confirmed → chain into loadout selection.
    hud.showLoadoutScreen((loadout) => startGame(username, mode, loadout));
};

const adminMode = urlParams.get('admin') === 'true';

if (prefilledUsername.length > 0 && prefilledUsername.length <= 16) {
    if (adminMode) {
        // Admin flow keeps the legacy "deploy instantly" UX so smoke tests can
        // bypass the loadout screen — admin mode adds its own custom drone set
        // server-side anyway.
        startGame(prefilledUsername, presetMode, [...DEFAULT_LOADOUT]);
    } else {
        // Username supplied via URL — skip the prompt but still let the player
        // pick a loadout.
        hud.showLoadoutScreen((loadout) =>
            startGame(prefilledUsername, presetMode, loadout),
        );
    }
} else {
    hud.showUsernamePrompt(handleUsernameSubmit, presetMode);
}

scene.startLoop((deltaMs: number) => {
    game.update(deltaMs);
});
